import { parse as opentypeParse, Font, Glyph } from "opentype.js";
import {
    dataUrlToArrayBuffer,
    domParser,
    fetchAsArrayBuffer,
    loadFontFaceSet,
    xmlSerializer,
} from "../util/crossplatform";
import { Declaration, parse, Rule } from "css";

const metricCache: Map<string, FontMetrics> = new Map();

/**
 * Stores cached information about metrics related to a specific font.
 */
export class FontMetrics {
    private font: Font;
    private glyphsFromText: Map<string, Glyph[]>;
    private kerningValues: Map<string, Map<string, number>>;
    private height: number;

    constructor(font: Font) {
        this.font = font;
        this.glyphsFromText = new Map();
        this.kerningValues = new Map();
        this.height = this.calculateApproximateHeight();
    }

    getFont(): Font {
        return this.font;
    }

    getGlyphs(text: string) {
        const oldGlyphs = this.glyphsFromText.get(text);
        if (oldGlyphs) {
            return oldGlyphs;
        }
        const newGlyphs = this.font.stringToGlyphs(text);
        this.glyphsFromText.set(text, newGlyphs);
        return newGlyphs;
    }

    /**
     * @returns The approximate height of any text line based on all main characters of the alphabet.
     */
    getApproximateHeight() {
        return this.height;
    }

    /**
     * Computes the exact height of a line of text.
     * @param text
     * @returns
     */
    getExactHeight(text: string): number {
        const glyphs = this.font.stringToGlyphs(text);
        let ascent = 0;
        let descent = 0;
        glyphs.forEach((glyph) => {
            const metrics = glyph.getMetrics();
            ascent = Math.max(ascent, metrics.yMax);
            descent = Math.min(descent, metrics.yMin);
        });
        return ascent - descent;
    }

    getKerningValue(a: Glyph, b: Glyph) {
        if (!a.name || !b.name) return undefined;
        let valuesForFirst = this.kerningValues.get(a.name);
        if (!valuesForFirst) {
            valuesForFirst = new Map();
            this.kerningValues.set(a.name, valuesForFirst);
        }
        let valueForSecond = valuesForFirst.get(b.name);
        if (!valueForSecond) {
            valueForSecond = this.font.getKerningValue(a, b);
            valuesForFirst.set(b.name, valueForSecond);
        }
        return valueForSecond;
    }

    private calculateApproximateHeight(): number {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        const glyphs = this.font.stringToGlyphs(chars);
        let ascent = 0;
        let descent = 0;
        glyphs.forEach((glyph) => {
            const metrics = glyph.getMetrics();
            ascent = Math.max(ascent, metrics.yMax);
            descent = Math.min(descent, metrics.yMin);
        });
        return ascent - descent;
    }
}

const isDataURL = (url: string) => {
    return url.substring(0, 5).toLowerCase().trim() === "data:";
};

const loadFontCache: Map<string, Promise<Font>> = new Map();
/**
 * A function for loading and caching a font
 * @param url The URL to load either a web URL or dataURL
 * @param buffer An optional array buffer for the resource if available
 * @returns An opentype font object.
 */
export const loadFont = async (url: string): Promise<Font> => {
    if (loadFontCache.has(url)) {
        return loadFontCache.get(url)!;
    }
    const getFont = async () => {
        try {
            const metrics = getFontMetrics(url);
            return metrics.getFont();
        } catch {
            if (isDataURL(url)) {
                const arrayBuffer = dataUrlToArrayBuffer(url);
                const font = opentypeParse(arrayBuffer);
                cacheFontMetrics(url, font);
                return font;
            } else {
                const buffer = await fetchAsArrayBuffer(url, true);
                const font = opentypeParse(buffer);
                cacheFontMetrics(url, font);
                await loadFontFaceSet(font, url);
                return font;
            }
        }
    };
    const promise = getFont();
    loadFontCache.set(url, promise);
    return promise;
};

/**
 * Loads a font object from a given data URL.
 * @deprecated Use loadFont instead.
 * @param url The data URL to load.
 * @returns A font object.
 */
export const _loadFontExternalDataURL = (url: string): Font => {
    const arrayBuffer = dataUrlToArrayBuffer(url);
    const font = opentypeParse(arrayBuffer);
    cacheFontMetrics(url, font);
    return font;
};

export const getFontMetrics = (assetUrl: string): FontMetrics => {
    const oldCache = metricCache.get(assetUrl);
    if (oldCache) {
        return oldCache;
    }
    throw new Error("Font metrics unavailable for font");
};

const cacheFontMetrics = (assetUrl: string, font: opentype.Font) => {
    const newCache = new FontMetrics(font);
    metricCache.set(assetUrl, newCache);
    return newCache;
};

export const outlineFontsInSvg = async (svg: string): Promise<string> => {
    const parser: DOMParser = domParser();
    const parsedSvg = parser.parseFromString(svg, "image/svg+xml");
    const root = parsedSvg.firstElementChild!;

    const fontMap = new Map(); // Map font names to URLs.
    const styles = root.querySelectorAll("style");
    styles.forEach((style) => {
        const tree = parse(style.innerHTML);
        tree.stylesheet?.rules.forEach((rule: Rule) => {
            if (rule.type === "font-face" && rule.declarations) {
                const fontFamily = (rule.declarations as Declaration[]).find(
                    (d) => d.property === "font-family",
                )?.value;
                const src = (rule.declarations as Declaration[]).find((d) => d.property === "src")?.value;
                const url = src?.match(/'(https.*?)'/)?.[1];
                if (fontFamily && url) {
                    fontMap.set(fontFamily, url);
                }
            }
        });
    });

    // Do the outlining.
    const tspans = parsedSvg.querySelectorAll("tspan");
    for (let i = 0; i < tspans.length; i++) {
        const tspan = tspans.item(i);
        const textAncestor = tspan.closest("text")!;
        const gAncestor = tspan.closest("g")!;
        const fontFamily = textAncestor.getAttribute("font-family")!;
        const font = await loadFont(fontMap.get(fontFamily));

        const fill = textAncestor.getAttribute("fill");
        const stroke = textAncestor.getAttribute("stroke");
        const strokeWidth = textAncestor.getAttribute("stroke-width");

        const pathToMeasure = font.getPath(
            tspan.innerHTML,
            0,
            0,
            parseInt(textAncestor.getAttribute("font-size") ?? "72"),
        );
        const width = pathToMeasure.getBoundingBox().x2 - pathToMeasure.getBoundingBox().x1;
        let offset = 0;
        const anchor = tspan.getAttribute("text-anchor");
        if (anchor === "middle") {
            offset = width / 2;
        } else if (anchor === "end") {
            offset = width;
        }
        const path = font.getPath(
            tspan.innerHTML,
            parseFloat(tspan.getAttribute("x") ?? "0") - offset,
            parseFloat(tspan.getAttribute("y") ?? "0"),
            parseInt(textAncestor.getAttribute("font-size") ?? "72")
        );

        path.fill = fill;
        path.stroke = stroke;
        path.strokeWidth = parseFloat(strokeWidth ?? "0");

        const pathString = path.toSVG(2);
        const pathDocument = parser.parseFromString(pathString, "image/svg+xml");
        const pathElement = pathDocument.firstElementChild!;
        gAncestor.appendChild(pathElement);
    }

    // Remove the old text elements.
    const texts = parsedSvg.querySelectorAll("text");
    texts.forEach((t) => t.remove());

    const serialiser = xmlSerializer();
    const rebuiltSvg = serialiser.serializeToString(root);
    return rebuiltSvg;
};
